import React, { useState } from 'react';
import { lightTheme as T } from '../../Constants/Colors';
import { fontFamily, fontSize, fontWeight } from '../../Constants/Fonts';
import ModuleIcon from '../../Components/ModuleIcon';

// AddOnCard — a single card tile with a tinted icon chip, a label, and a
// trailing action. Fully theme-driven from the JS tokens in
// Constants/Colors + Constants/Fonts.
//
// Two ways to drive the trailing action:
//   • default (add-on) mode — pass `active` + `onEnable`; renders an
//     "Active" badge or a clickable "Enable" action.
//   • custom mode — pass `action` (a React node, e.g. a download icon or a
//     loader); it fully replaces the Active/Enable rendering. Combine with
//     `onClick` to make the whole card clickable.
//
// Props:
//   icon     — optional ModuleIcon name rendered in the chip; omit for no chip
//   iconNode — optional custom node for the chip (e.g. an <img> logo); wins over `icon`
//   label    — the tile label
//   active   — true → "Active" badge; false → "Enable" action (default mode)
//   onEnable — click handler for the Enable action (ignored when active)
//   action   — custom trailing node; overrides active/onEnable when provided
//   onClick  — card click handler (used with `action`)
//   title    — native tooltip on the card
//   iconColor — override the icon glyph colour (defaults to primary navy)
//   iconBg    — override the icon chip background (defaults to primarySoft)
//   align     — cross-axis alignment of chip/label/action ('center' default);
//               pass 'flex-start' for multi-line labels (name + description)
//   className — optional extra class on the root
//   style     — optional style overrides merged onto the root (e.g. height:100%
//               so tiles in a grid row stretch to equal height)
function AddOnCard({ icon, iconNode, label, active, onEnable, action, onClick, title, iconColor, iconBg, align = 'center', className, style }) {
    const [hover, setHover] = useState(false);
    const custom = action !== undefined;
    const clickHandler = custom ? onClick : (!active ? onEnable : undefined);
    const interactive = typeof clickHandler === 'function';
    return (
        <div
            onClick={clickHandler}
            title={title}
            className={className}
            onMouseEnter={interactive ? () => setHover(true) : undefined}
            onMouseLeave={interactive ? () => setHover(false) : undefined}
            style={{
                display: 'flex',
                alignItems: align,
                gap: 12,
                background: T.surface,
                border: `1px solid ${hover ? T.borderStrong : T.border}`,
                borderRadius: T.radiusLg,
                boxShadow: hover ? T.shadow : T.shadowSm,
                padding: '14px 16px',
                minWidth: 0,
                cursor: interactive ? 'pointer' : 'default',
                transition: 'box-shadow .15s ease, border-color .15s ease',
                ...style,
            }}
        >
            {(iconNode || icon) && (
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: '0 0 auto',
                        width: 40,
                        height: 40,
                        borderRadius: T.radius,
                        background: iconBg || T.primarySoft,
                    }}
                >
                    {iconNode || <ModuleIcon name={icon} size={20} color={iconColor || T.primary} />}
                </span>
            )}

            <span
                style={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily,
                    fontSize: fontSize.md,
                    fontWeight: fontWeight.medium,
                    color: T.textPrimary,
                }}
            >
                {label}
            </span>

            {custom ? (
                <span style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center' }}>
                    {action}
                </span>
            ) : active ? (
                <span
                    style={{
                        flex: '0 0 auto',
                        fontFamily,
                        fontSize: fontSize.sm,
                        fontWeight: fontWeight.semibold,
                        color: T.success,
                        background: T.successSoft,
                        padding: '4px 10px',
                        borderRadius: T.radiusPill,
                    }}
                >
                    Active
                </span>
            ) : (
                <span
                    role="button"
                    style={{
                        flex: '0 0 auto',
                        fontFamily,
                        fontSize: fontSize.sm,
                        fontWeight: fontWeight.semibold,
                        color: T.primary,
                    }}
                >
                    Enable
                </span>
            )}
        </div>
    );
}

export default AddOnCard;
